import { TAB_PAGES } from "@/lib/tabs/pageDescriptors";
import {
  buildId,
  makeTab,
  withTitleOverride,
} from "@/lib/tabs/registryCore";
import { normalizeMarketplaceFilters } from "@/lib/marketplaceFilters";
import { getAdminNewsArticleBySlug } from "@/lib/api/news";
import { adminNewsArticleEditTab } from "@/lib/tabs/registryAdminTabs";
import { hasNewsEditorAccess } from "@/components/pages/admin/News/newsAdminAccess";

export const externalInvitesId = () => buildId("external", "invites");
export const externalInvitesTab = (search, role) =>
  makeTab({
    descriptor: TAB_PAGES.ExternalInvitesPage,
    idParts: ["external", "invites"],
    title: "tabs.users.external_invites",
    params: { search, role },
  });

export const userId = (userIdValue) => buildId("user", userIdValue);
export const userPageTab = (userIdValue, firstName, lastName) =>
  makeTab({
    descriptor: TAB_PAGES.UserPage,
    idParts: ["user", userIdValue],
    title: `${firstName} ${lastName}`.trim(),
    params: { userId: userIdValue },
  });

export const amainvoiceId = () => buildId("tools", "amainvoice");
export const amainvoiceTab = (url) =>
  makeTab({
    descriptor: TAB_PAGES.AmainvoicePage,
    idParts: ["tools", "amainvoice"],
    title: "tabs.tools.amainvoice",
    params: { url },
  });

export const billbeeId = () => buildId("tools", "billbee");
export const billbeeTab = (url) =>
  makeTab({
    descriptor: TAB_PAGES.BillbeePage,
    idParts: ["tools", "billbee"],
    title: "tabs.tools.billbee",
    params: { url },
  });

export const replycoId = () => buildId("tools", "replyco");
export const replycoTab = (url) =>
  makeTab({
    descriptor: TAB_PAGES.ReplycoPage,
    idParts: ["tools", "replyco"],
    title: "tabs.tools.replyco",
    params: { url },
  });

export const ventoryOneId = () => buildId("tools", "ventoryone");
export const ventoryOneTab = (url) =>
  makeTab({
    descriptor: TAB_PAGES.VentoryOnePage,
    idParts: ["tools", "ventoryone"],
    title: "tabs.tools.ventory_one",
    params: { url },
  });

export const dashboardId = () => buildId("dashboard");
export const dashboardTab = () =>
  makeTab({
    descriptor: TAB_PAGES.DashboardPage,
    idParts: ["dashboard"],
    title: "tabs.dashboard",
    params: {},
  });

export const marketplaceId = () => buildId("marketplace");
export const marketplaceTab = () =>
  makeTab({
    descriptor: TAB_PAGES.MarketplacePage,
    idParts: ["marketplace"],
    title: "tabs.marketplace",
    params: {},
  });

export const newsId = () => buildId("news");
export const newsTab = () =>
  makeTab({
    descriptor: TAB_PAGES.NewsPage,
    idParts: ["news"],
    title: "tabs.news",
    params: {},
  });

export const newsArticleId = (slug) => buildId("news", "article", slug);
export const newsArticleTab = (slug) =>
  makeTab({
    descriptor: TAB_PAGES.NewsPage,
    idParts: ["news"],
    title: "tabs.news",
    params: { articleSlug: slug },
  });

export const servicePageId = (serviceId) => buildId("service", serviceId);
export const servicePageTab = (serviceId, serviceName, filters = {}) =>
  makeTab({
    descriptor: TAB_PAGES.ServicePage,
    idParts: ["service", serviceId],
    title: serviceName,
    params: { serviceId, ...normalizeMarketplaceFilters(filters) },
  });

export const partnerPageId = (partnerId) => buildId("partner", partnerId);
export const partnerPageTab = (partnerId, partnerName) =>
  makeTab({
    descriptor: TAB_PAGES.PartnerPage,
    idParts: ["partner", partnerId],
    title: partnerName,
    params: { partnerId },
  });

export const usersPageId = () => buildId("users");
export const usersPageTab = () =>
  makeTab({
    descriptor: TAB_PAGES.UsersPage,
    idParts: ["users"],
    title: "tabs.users.list",
    params: {},
  });

export const companyPageId = () => buildId("company");
export const companyPageTab = () =>
  makeTab({
    descriptor: TAB_PAGES.CompanyPage,
    idParts: ["company"],
    title: "tabs.company",
    params: {},
  });

export const organizationsId = () => buildId("organizations");
export const organizationsTab = () =>
  makeTab({
    descriptor: TAB_PAGES.OrganizationsPage,
    idParts: ["organizations"],
    title: "tabs.organizations",
    params: {},
  });

export const logsId = () => buildId("logs");
export const logsTab = () =>
  makeTab({
    descriptor: TAB_PAGES.LogsPage,
    idParts: ["logs"],
    title: "tabs.logs",
    params: {},
  });

const staticTabBuilders = {
  [marketplaceId()]: () => marketplaceTab(),
  [newsId()]: () => newsTab(),
  [amainvoiceId()]: () => amainvoiceTab(),
  [billbeeId()]: () => billbeeTab(),
  [replycoId()]: () => replycoTab(),
  [ventoryOneId()]: () => ventoryOneTab(),
  [dashboardId()]: () => dashboardTab(),
  [usersPageId()]: () => usersPageTab(),
  [companyPageId()]: () => companyPageTab(),
  [organizationsId()]: () => organizationsTab(),
  [logsId()]: () => logsTab(),
  [externalInvitesId()]: () => externalInvitesTab(undefined, undefined),
};

const dynamicTabResolvers = [
  {
    matches: (parts) => parts[0] === "user" && parts[1],
    create: (parts, titleOverride) =>
      withTitleOverride(userPageTab(parts[1], "", ""), titleOverride, parts[1]),
  },
  {
    matches: (parts) => parts[0] === "service" && parts[1],
    create: (parts, titleOverride) =>
      withTitleOverride(
        servicePageTab(parts[1], titleOverride || parts[1]),
        null,
        parts[1],
      ),
  },
  {
    matches: (parts) => parts[0] === "partner" && parts[1],
    create: (parts, titleOverride) =>
      withTitleOverride(
        partnerPageTab(parts[1], titleOverride || parts[1]),
        null,
        parts[1],
      ),
  },
  {
    matches: (parts) =>
      parts[0] === "news" && parts[1] === "article" && parts[2],
    create: async (parts, titleOverride, context = {}) => {
      const slug = parts.slice(2).join(":");

      if (hasNewsEditorAccess(context.auth)) {
        const response = await getAdminNewsArticleBySlug(slug, {
          languageCode: context.languageCode,
        });
        const article = response.data;

        return withTitleOverride(
          adminNewsArticleEditTab(
            article.id,
            article.title || titleOverride,
            context.languageCode,
          ),
          null,
          titleOverride || article.id,
        );
      }

      return withTitleOverride(newsArticleTab(slug), null, "tabs.news");
    },
  },
];

export default {
  dynamicTabResolvers,
  staticTabBuilders,
};
